import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { colors } from "../styles/colors";
import { typography } from "../styles/typography";
import { globalStyles } from "../styles/globalStyles";
import bookingAPI from "../services/bookingApi";
import { useUser } from "../context/UserContext";

// Responsive helper functions (adapted for web)
const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: userLoading, isAdmin } = useUser();
  
  const [bookings, setBookings] = useState([]);
  const [halls, setHalls] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [loading, setLoading] = useState(true);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Priority booking states
  const [priorityModalVisible, setPriorityModalVisible] = useState(false);
  const [priorityBookingData, setPriorityBookingData] = useState({
    hall: '',
    date: '',
    time: '',
    department: '',
    purpose: '',
    requester: '',
    otherReason: ''
  });
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  
  // Reports states
  const [reportsModalVisible, setReportsModalVisible] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  
  useEffect(() => {
    // // console.log('ADMIN DEBUG - useEffect triggered');
    // // console.log('ADMIN DEBUG - userLoading:', userLoading);
    // // console.log('ADMIN DEBUG - isAuthenticated:', isAuthenticated);
    // // console.log('ADMIN DEBUG - user:', user);
    // // console.log('ADMIN DEBUG - isAdmin():', isAdmin());
    
    if (!userLoading && (!isAuthenticated || !isAdmin())) {
      // // console.log('ADMIN DEBUG - Authentication failed, redirecting to login');
      alert('Please log in as an administrator to access this page.');
      navigate('/');
      return;
    }
    if (!userLoading && isAuthenticated && isAdmin()) {
      // // console.log('ADMIN DEBUG - Authentication successful, loading data');
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userLoading, user, navigate, isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadBookings(),
        loadHalls(),
        loadDashboardStats()
      ]);
    } catch (error) {
      console.error('Error loading admin data:', error);
      alert('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await bookingAPI.getAllBookings();
      if (response.success) {
        setBookings(response.data || []);
        // console.log('ADMIN DEBUG - Bookings loaded:', response.data);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadHalls = async () => {
    try {
      const response = await bookingAPI.getHalls();
      if (response.success) {
        setHalls(response.data || []);
      }
    } catch (error) {
      console.error('Error loading halls:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const response = await bookingAPI.getDashboardStats();
      if (response.success) {
        setDashboardStats(response.data || {});
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const handleApproveBooking = async (bookingId) => {
    try {
      // // console.log('Approving booking with ID:', bookingId);
      const response = await bookingAPI.updateBookingStatus(bookingId, 'approved');
      if (response.success) {
        alert('Booking approved successfully!');
        loadBookings();
      } else {
        alert('Failed to approve booking');
      }
    } catch (error) {
      console.error('Error approving booking:', error);
      alert('Error approving booking');
    }
  };

  const handleRejectBooking = async () => {
    if (!selectedBooking || !rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const response = await bookingAPI.updateBookingStatus(selectedBooking.booking_id, 'rejected', rejectReason);
      if (response.success) {
        alert('Booking rejected');
        loadBookings();
        setModalVisible(false);
        setSelectedBooking(null);
        setRejectReason('');
      } else {
        alert('Failed to reject booking');
      }
    } catch (error) {
      console.error('Error rejecting booking:', error);
      alert('Error rejecting booking');
    }
  };

  const openRejectModal = (booking) => {
    setSelectedBooking(booking);
    setModalVisible(true);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      const keysToRemove = ['authToken', 'userRole', 'userName','userData'];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      navigate('/');
    }
  };

  // Priority Booking Functions
  const handleCreatePriorityBooking = () => {
    setPriorityModalVisible(true);
  };

  const handlePriorityBookingSubmit = async () => {
    try {
      if (!priorityBookingData.hall || !priorityBookingData.date || !priorityBookingData.time || !priorityBookingData.requester) {
        alert('Please fill in all required fields');
        return;
      }

      const bookingData = {
        hall_id: priorityBookingData.hall,
        booking_date: priorityBookingData.date,
        time_slot_id: priorityBookingData.time,
        requester_name: priorityBookingData.requester,
        event_title: 'Priority Booking',
        purpose: priorityBookingData.purpose,
        department: priorityBookingData.department,
        priority: true,
        status: 'approved', // Priority bookings are auto-approved
        notes: `Priority booking created by admin. Reason: ${priorityBookingData.otherReason || 'Admin priority request'}`
      };

      const response = await bookingAPI.submitBooking(bookingData);
      if (response.success) {
        alert('Priority booking created successfully!');
        setPriorityModalVisible(false);
        setPriorityBookingData({
          hall: '',
          date: '',
          time: '',
          department: '',
          purpose: '',
          requester: '',
          otherReason: ''
        });
        loadBookings();
      } else {
        alert(response.error || 'Failed to create priority booking');
      }
    } catch (error) {
      console.error('Error creating priority booking:', error);
      alert('Error creating priority booking');
    }
  };

  const loadTimeSlotsForPriority = async (hallId, date) => {
    if (!hallId || !date) return;
    
    try {
      setLoadingTimeSlots(true);
      const response = await bookingAPI.getTimeSlots(hallId, date, null);
      
      if (response.success) {
        setAvailableTimeSlots(response.data || []);
      } else {
        // Provide default time slots
        setAvailableTimeSlots([
          { id: '1', time: '8:45 AM - 10:45 AM', available: true },
          { id: '2', time: '11:00 AM - 12:45 PM', available: true },
          { id: '3', time: '1:00 PM - 3:45 PM', available: true },
          { id: '4', time: 'Full Day', available: true }
        ]);
      }
    } catch (error) {
      console.error('Error loading time slots:', error);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // Reports Functions
  const handleGenerateReports = () => {
    setReportsModalVisible(true);
  };

  // Manage Admin Functions
  const handleManageAdmins = () => {
    navigate('/admin/manage-admins');
  };

  const generateReport = async (reportType, format) => {
    setGeneratingReport(true);
    try {
      let response;
      const startDate = reportDateRange.startDate.toISOString().split('T')[0];
      const endDate = reportDateRange.endDate.toISOString().split('T')[0];

      switch (reportType) {
        case 'bookings':
          response = await bookingAPI.generateBookingsReport(startDate, endDate, format);
          break;
        case 'halls':
          response = await bookingAPI.generateHallUtilizationReport(startDate, endDate, format);
          break;
        case 'analytics':
          response = await bookingAPI.generateAnalyticsReport(startDate, endDate, format);
          break;
        default:
          throw new Error('Invalid report type');
      }

      if (response.success && response.blob) {
        downloadFile(response.blob, `${reportType}_report_${startDate}_to_${endDate}.${format}`);
        alert('Report generated successfully!');
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return colors.success;
      case 'rejected': return colors.danger;
      case 'pending': return colors.warning;
      default: return colors.gray;
    }
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const recentBookings = bookings.slice(0, 3);

  // Show loading while checking authentication
  if (userLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Verifying admin access...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.welcomeSection}>
              <div style={styles.adminIcon}>👨‍💼</div>
              <div>
                <div style={styles.welcomeText}>Welcome back,</div>
                <div style={styles.adminName}>{user?.name || 'Admin'}</div>
                <div style={styles.adminRole}>{user?.role || 'Administrator'}</div>
              </div>
            </div>
            <button style={styles.logoutButton} onClick={handleLogout}>
              <span style={styles.logoutButtonText}>🚪 Logout</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📅</div>
            <div style={styles.statValue}>{dashboardStats.totalBookings || bookings.length}</div>
            <div style={styles.statLabel}>Total Bookings</div>
          </div>
          
          <div style={styles.statCard}>
            <div style={styles.statIcon}>⏳</div>
            <div style={styles.statValue}>{pendingBookings.length}</div>
            <div style={styles.statLabel}>Pending Approval</div>
          </div>
          
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🏛️</div>
            <div style={styles.statValue}>{halls.length}</div>
            <div style={styles.statLabel}>Total Halls</div>
          </div>
          
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statValue}>{bookings.filter(b => b.status === 'approved').length}</div>
            <div style={styles.statLabel}>Approved Today</div>
          </div>
        </div>

        {/* Pending Bookings Section */}
        {pendingBookings.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitle}>⏳ Pending Approvals ({pendingBookings.length})</div>
            </div>
            
            <div style={styles.bookingsGrid}>
              {pendingBookings.map((booking) => (
                <div key={booking.id} style={styles.bookingCard}>
                  <div style={styles.bookingHeader}>
                    <div style={styles.bookingTitle}>{booking.event_title || booking.eventTitle}</div>
                    <div style={{...styles.statusBadge, backgroundColor: getStatusColor(booking.status)}}>
                      <span style={styles.statusText}>{booking.status}</span>
                    </div>
                  </div>
                  
                  <div style={styles.bookingDetails}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Date:</span>
                      <span style={styles.detailValue}>{formatDate(booking.booking_date)}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Hall:</span>
                      <span style={styles.detailValue}>{booking.hall?.location || 'N/A'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Requester:</span>
                      <span style={styles.detailValue}>{booking.requester_name || booking.requesterName || booking.name || 'N/A'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Attendees:</span>
                      <span style={styles.detailValue}>{booking.attendees} people</span>
                    </div>
                  </div>
                  
                  <div style={styles.bookingActions}>
                    <button 
                      style={styles.approveButton}
                      onClick={() => handleApproveBooking(booking.booking_id)}
                    >
                      <span style={styles.approveButtonText}>✅ Approve</span>
                    </button>
                    <button 
                      style={styles.rejectButton}
                      onClick={() => openRejectModal(booking)}
                    >
                      <span style={styles.rejectButtonText}>❌ Reject</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Actions */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>🛠️ Admin Actions</div>
          </div>
          
          <div style={styles.adminActionsGrid}>
            <button 
              style={styles.actionButton} 
              onClick={handleCreatePriorityBooking}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={styles.actionIcon}>⚡</div>
              <div style={styles.actionText}>Priority Booking</div>
              <div style={styles.actionSubtext}>Create urgent bookings</div>
            </button>
            
            <button 
              style={styles.actionButton} 
              onClick={handleGenerateReports}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={styles.actionIcon}>📊</div>
              <div style={styles.actionText}>Generate Reports</div>
              <div style={styles.actionSubtext}>Analytics & statistics</div>
            </button>
            
            <button 
              style={styles.actionButton} 
              onClick={() => navigate('/admin/bookings')}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={styles.actionIcon}>📋</div>
              <div style={styles.actionText}>Manage Bookings</div>
              <div style={styles.actionSubtext}>View all bookings</div>
            </button>
            
            <button 
              style={styles.actionButton} 
              onClick={handleManageAdmins}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={styles.actionIcon}>👥</div>
              <div style={styles.actionText}>Manage Admins</div>
              <div style={styles.actionSubtext}>Add or remove admins</div>
            </button>
          </div>
        </div>

        {/* Recent Bookings */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>📋 Recent Bookings</div>
            <button 
              style={styles.viewAllButton}
              onClick={() => navigate('/admin/bookings')}
            >
              <span style={styles.viewAllButtonText}>View All</span>
            </button>
          </div>
          
          <div style={styles.bookingsGrid}>
            {recentBookings.map((booking) => (
              <div key={booking.id} style={styles.bookingCard}>
                <div style={styles.bookingHeader}>
                  <div style={styles.bookingTitle}>{booking.event_title || booking.eventTitle}</div>
                  <div style={{...styles.statusBadge, backgroundColor: getStatusColor(booking.status)}}>
                    <span style={styles.statusText}>{booking.status}</span>
                  </div>
                </div>
                
                <div style={styles.bookingDetails}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Date:</span>
                    <span style={styles.detailValue}>{formatDate(booking.booking_date)}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Hall:</span>
                    <span style={styles.detailValue}>{booking.hall_name || booking.hallName || booking.hall?.location|| 'N/A'}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Requester:</span>
                    <span style={styles.detailValue}>{booking.requester_name || booking.requesterName || booking.name || 'N/A'}</span>
                  </div>
                </div>
                
                {booking.status === 'approved' && (
                  <div style={styles.bookingActions}>
                    <button 
                      style={styles.rejectApprovedButton}
                      onClick={() => openRejectModal(booking)}
                    >
                      <span style={styles.rejectButtonText}>❌ Reject</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {modalVisible && (
        <div style={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Reject Booking</div>
            </div>
            
            <div style={styles.modalContent}>
              <div style={styles.modalText}>
                Please provide a reason for rejecting this booking request:
              </div>
              
              <textarea
                style={styles.textArea}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={4}
              />
            </div>
            
            <div style={styles.modalActions}>
              <button 
                style={styles.cancelButton}
                onClick={() => setModalVisible(false)}
              >
                <span style={styles.cancelButtonText}>Cancel</span>
              </button>
              <button 
                style={styles.confirmRejectButton}
                onClick={handleRejectBooking}
              >
                <span style={styles.confirmRejectButtonText}>Reject Booking</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Priority Booking Modal */}
      {priorityModalVisible && (
        <div style={styles.modalOverlay} onClick={() => setPriorityModalVisible(false)}>
          <div style={styles.priorityModalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>⚡ Create Priority Booking</div>
              <button style={styles.closeButton} onClick={() => setPriorityModalVisible(false)}>×</button>
            </div>
            
            <div style={styles.priorityModalContent}>
              <div style={styles.priorityFormGrid}>
                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Hall *</label>
                  <select 
                    style={styles.prioritySelect}
                    value={priorityBookingData.hall}
                    onChange={(e) => {
                      const newData = {...priorityBookingData, hall: e.target.value};
                      setPriorityBookingData(newData);
                      if (e.target.value && priorityBookingData.date) {
                        loadTimeSlotsForPriority(e.target.value, priorityBookingData.date);
                      }
                    }}
                  >
                    <option value="">Select Hall</option>
                    {halls.map(hall => (
                      <option key={hall.id} value={hall.id}>{hall.name}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Date *</label>
                  <input 
                    type="date"
                    style={styles.priorityInput}
                    value={priorityBookingData.date}
                    onChange={(e) => {
                      const newData = {...priorityBookingData, date: e.target.value};
                      setPriorityBookingData(newData);
                      if (priorityBookingData.hall && e.target.value) {
                        loadTimeSlotsForPriority(priorityBookingData.hall, e.target.value);
                      }
                    }}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Time Slot *</label>
                  <select 
                    style={styles.prioritySelect}
                    value={priorityBookingData.time}
                    onChange={(e) => setPriorityBookingData({...priorityBookingData, time: e.target.value})}
                    disabled={loadingTimeSlots || availableTimeSlots.length === 0}
                  >
                    <option value="">
                      {loadingTimeSlots ? 'Loading...' : 'Select Time Slot'}
                    </option>
                    {availableTimeSlots.map(slot => (
                      <option key={slot.id} value={slot.id}>{slot.time}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Requester Name *</label>
                  <input 
                    type="text"
                    style={styles.priorityInput}
                    value={priorityBookingData.requester}
                    onChange={(e) => setPriorityBookingData({...priorityBookingData, requester: e.target.value})}
                    placeholder="Enter requester name"
                  />
                </div>

                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Department</label>
                  <input 
                    type="text"
                    style={styles.priorityInput}
                    value={priorityBookingData.department}
                    onChange={(e) => setPriorityBookingData({...priorityBookingData, department: e.target.value})}
                    placeholder="Enter department"
                  />
                </div>

                <div style={styles.priorityFormGroup}>
                  <label style={styles.priorityLabel}>Purpose</label>
                  <textarea 
                    style={styles.priorityTextarea}
                    value={priorityBookingData.purpose}
                    onChange={(e) => setPriorityBookingData({...priorityBookingData, purpose: e.target.value})}
                    placeholder="Enter purpose"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            
            <div style={styles.priorityModalActions}>
              <button style={styles.cancelButton} onClick={() => setPriorityModalVisible(false)}>
                Cancel
              </button>
              <button style={styles.createButton} onClick={handlePriorityBookingSubmit}>
                Create Priority Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reports Modal */}
      {reportsModalVisible && (
        <div style={styles.modalOverlay} onClick={() => setReportsModalVisible(false)}>
          <div style={styles.reportsModalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>📊 Generate Reports</div>
              <button style={styles.closeButton} onClick={() => setReportsModalVisible(false)}>×</button>
            </div>
            
            <div style={styles.reportsModalContent}>
              <div style={styles.dateRangeSection}>
                <div style={styles.dateRangeTitle}>📅 Date Range</div>
                <div style={styles.dateRangeInputs}>
                  <div style={styles.dateInputGroup}>
                    <label style={styles.dateLabel}>From:</label>
                    <input 
                      type="date"
                      style={styles.dateInput}
                      value={reportDateRange.startDate.toISOString().split('T')[0]}
                      onChange={(e) => setReportDateRange({
                        ...reportDateRange, 
                        startDate: new Date(e.target.value)
                      })}
                    />
                  </div>
                  <div style={styles.dateInputGroup}>
                    <label style={styles.dateLabel}>To:</label>
                    <input 
                      type="date"
                      style={styles.dateInput}
                      value={reportDateRange.endDate.toISOString().split('T')[0]}
                      onChange={(e) => setReportDateRange({
                        ...reportDateRange, 
                        endDate: new Date(e.target.value)
                      })}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.reportsGrid}>
                <div style={styles.reportCard}>
                  <div style={styles.reportIcon}>📋</div>
                  <div style={styles.reportTitle}>Bookings Report</div>
                  <div style={styles.reportDescription}>Complete list of all bookings with details</div>
                  <div style={styles.reportActions}>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('bookings', 'pdf')}
                      disabled={generatingReport}
                    >
                      PDF
                    </button>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('bookings', 'xlsx')}
                      disabled={generatingReport}
                    >
                      Excel
                    </button>
                  </div>
                </div>

                <div style={styles.reportCard}>
                  <div style={styles.reportIcon}>🏛️</div>
                  <div style={styles.reportTitle}>Hall Utilization</div>
                  <div style={styles.reportDescription}>Usage statistics for all halls</div>
                  <div style={styles.reportActions}>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('halls', 'pdf')}
                      disabled={generatingReport}
                    >
                      PDF
                    </button>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('halls', 'xlsx')}
                      disabled={generatingReport}
                    >
                      Excel
                    </button>
                  </div>
                </div>

                <div style={styles.reportCard}>
                  <div style={styles.reportIcon}>📈</div>
                  <div style={styles.reportTitle}>Analytics Report</div>
                  <div style={styles.reportDescription}>Trends and insights dashboard</div>
                  <div style={styles.reportActions}>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('analytics', 'pdf')}
                      disabled={generatingReport}
                    >
                      PDF
                    </button>
                    <button 
                      style={styles.reportButton}
                      onClick={() => generateReport('analytics', 'xlsx')}
                      disabled={generatingReport}
                    >
                      Excel
                    </button>
                  </div>
                </div>
              </div>

              {generatingReport && (
                <div style={styles.generatingReport}>
                  <div style={styles.spinner}></div>
                  <span>Generating report...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  spinner: {
    width: 40,
    height: 40,
    border: `4px solid ${colors.border}`,
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: 10,
  },
  header: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(16, 20, 32),
    paddingBottom: getResponsiveValue(24, 32, 40),
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  welcomeSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  adminIcon: {
    fontSize: getResponsiveValue(40, 50, 60),
    marginRight: getResponsiveValue(12, 16, 20),
  },
  welcomeText: {
    ...typography.body,
    color: colors.white,
    opacity: 0.9,
  },
  adminName: {
    fontSize: getResponsiveValue(20, 24, 28),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 4,
  },
  adminRole: {
    ...typography.small,
    color: colors.white,
    opacity: 0.8,
  },
  logoutButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  logoutButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  statsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: getResponsiveValue(16, 20, 32),
    gap: getResponsiveValue(12, 16, 20),
  },
  statCard: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(12, 16, 20),
    padding: getResponsiveValue(16, 20, 24),
    flex: window.innerWidth >= 1024 ? '0 0 calc(25% - 20px)' : window.innerWidth >= 768 ? '0 0 calc(50% - 20px)' : '0 0 calc(50% - 12px)',
    minWidth: 120,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...globalStyles.shadow,
  },
  statIcon: {
    fontSize: getResponsiveValue(24, 32, 40),
    marginBottom: getResponsiveValue(8, 12, 16),
  },
  statValue: {
    fontSize: getResponsiveValue(20, 28, 36),
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: getResponsiveValue(4, 6, 8),
  },
  statLabel: {
    ...typography.caption,
    color: colors.textLight,
    textAlign: 'center',
    fontSize: getResponsiveValue(12, 14, 16),
  },
  section: {
    padding: getResponsiveValue(16, 20, 32),
    paddingTop: 0,
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  sectionTitle: {
    fontSize: getResponsiveValue(18, 22, 26),
    fontWeight: 'bold',
    color: colors.text,
  },
  viewAllButton: {
    backgroundColor: colors.secondary,
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  viewAllButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  bookingsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: getResponsiveValue(12, 16, 20),
  },
  bookingCard: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(8, 12, 16),
    padding: getResponsiveValue(16, 20, 24),
    flex: window.innerWidth >= 1024 ? '0 0 calc(33.333% - 20px)' : window.innerWidth >= 768 ? '0 0 calc(50% - 20px)' : '0 0 100%',
    minWidth: 280,
    ...globalStyles.shadow,
  },
  bookingHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  bookingTitle: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: 'bold',
    color: colors.text,
    flex: 1,
    marginRight: 12,
  },
  statusBadge: {
    paddingLeft: getResponsiveValue(6, 8, 10),
    paddingRight: getResponsiveValue(6, 8, 10),
    paddingTop: getResponsiveValue(2, 3, 4),
    paddingBottom: getResponsiveValue(2, 3, 4),
    borderRadius: 12,
  },
  statusText: {
    color: colors.white,
    fontSize: getResponsiveValue(10, 11, 12),
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  bookingDetails: {
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(4, 6, 8),
  },
  detailLabel: {
    ...typography.small,
    color: colors.textLight,
    fontWeight: '500',
  },
  detailValue: {
    ...typography.small,
    color: colors.text,
    fontWeight: '600',
  },
  bookingActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(8, 10, 12),
  },
  approveButton: {
    backgroundColor: colors.success,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
    flex: 1,
  },
  approveButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  rejectButton: {
    backgroundColor: colors.danger,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
    flex: 1,
  },
  rejectButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  rejectApprovedButton: {
    backgroundColor: colors.danger,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
    width: '100%',
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(12, 16, 20),
    padding: getResponsiveValue(20, 24, 32),
    minWidth: getResponsiveValue(300, 400, 500),
    maxWidth: '90%',
    ...globalStyles.shadow,
  },
  modalHeader: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  modalTitle: {
    fontSize: getResponsiveValue(18, 20, 24),
    fontWeight: 'bold',
    color: colors.text,
  },
  modalContent: {
    marginBottom: getResponsiveValue(20, 24, 32),
  },
  modalText: {
    ...typography.body,
    color: colors.text,
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  textArea: {
    width: '100%',
    minHeight: getResponsiveValue(80, 100, 120),
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: getResponsiveValue(10, 12, 16),
  },
  cancelButton: {
    backgroundColor: colors.gray,
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  cancelButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  confirmRejectButton: {
    backgroundColor: colors.danger,
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  confirmRejectButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },

  // Admin Actions styles
  adminActionsGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(16, 20, 24),
    flexWrap: 'wrap',
  },
  actionButton: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(12, 16, 20),
    padding: getResponsiveValue(20, 24, 32),
    border: `2px solid ${colors.border}`,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1',
    minWidth: 200,
    transition: 'all 0.2s ease',
    ...globalStyles.shadow,
  },
  actionIcon: {
    fontSize: getResponsiveValue(32, 40, 48),
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  actionText: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    textAlign: 'center',
  },
  actionSubtext: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    textAlign: 'center',
  },

  // Priority Booking Modal styles
  priorityModalContainer: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(16, 20, 24),
    padding: getResponsiveValue(24, 32, 40),
    minWidth: getResponsiveValue(400, 600, 800),
    maxWidth: '90%',
    maxHeight: '90%',
    overflowY: 'auto',
    ...globalStyles.shadow,
  },
  priorityModalContent: {
    marginBottom: getResponsiveValue(24, 32, 40),
  },
  priorityFormGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: getResponsiveValue(16, 20, 24),
  },
  priorityFormGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  priorityLabel: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
  },
  priorityInput: {
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    outline: 'none',
  },
  prioritySelect: {
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    cursor: 'pointer',
    outline: 'none',
  },
  priorityTextarea: {
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none',
  },
  priorityModalActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: getResponsiveValue(12, 16, 20),
  },
  createButton: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(12, 16, 20),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: 24,
    color: colors.textLight,
    cursor: 'pointer',
    width: 32,
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Reports Modal styles
  reportsModalContainer: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(16, 20, 24),
    padding: getResponsiveValue(24, 32, 40),
    minWidth: getResponsiveValue(500, 700, 900),
    maxWidth: '90%',
    maxHeight: '90%',
    overflowY: 'auto',
    ...globalStyles.shadow,
  },
  reportsModalContent: {
    marginBottom: getResponsiveValue(24, 32, 40),
  },
  dateRangeSection: {
    marginBottom: getResponsiveValue(24, 32, 40),
    padding: getResponsiveValue(16, 20, 24),
    backgroundColor: colors.light,
    borderRadius: getResponsiveValue(8, 12, 16),
  },
  dateRangeTitle: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  dateRangeInputs: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(16, 20, 24),
    flexWrap: 'wrap',
  },
  dateInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 150,
  },
  dateLabel: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
  },
  dateInput: {
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    outline: 'none',
  },
  reportsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: getResponsiveValue(16, 20, 24),
  },
  reportCard: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 12, 16),
    padding: getResponsiveValue(20, 24, 32),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue(16, 20, 24),
  },
  reportIcon: {
    fontSize: getResponsiveValue(32, 40, 48),
  },
  reportTitle: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    flex: 1,
  },
  reportDescription: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    flex: 1,
  },
  reportActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(8, 10, 12),
  },
  reportButton: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(4, 6, 8),
    fontSize: getResponsiveValue(12, 14, 16),
    cursor: 'pointer',
    fontWeight: '600',
    minWidth: 60,
  },
  generatingReport: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: getResponsiveValue(16, 20, 24),
    backgroundColor: colors.light,
    borderRadius: getResponsiveValue(8, 12, 16),
    marginTop: getResponsiveValue(16, 20, 24),
  },
};

export default AdminDashboardPage;